import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectionListChange } from '@angular/material/list';
import { BuiltInFieldId } from '../../../models/built-in-field-id.enum';
import { FieldLayoutViewModel, FieldViewModel } from '../../../models/field/field-layout-view-model.model';
import { FieldType } from '../../../models/field/field-type.enum';
import { FieldLayoutViewModelService } from '../../../services/field-layout-view-model.service';
import { KeyHandlerDirective } from '../../../directives/key-handler.directive';

@Component({
    selector: 'lex-select-fields',
    templateUrl: './select-fields.component.html',
    styleUrls: ['./select-fields.component.scss'],
    standalone: false
})
export class SelectFieldsDialogComponent extends KeyHandlerDirective {
	allFields: FieldViewModel[] = [];
	availableFields: FieldViewModel[] = [];
	availableFieldsFiltered: FieldViewModel[] = [];
	selectedFields: FieldViewModel[] = [];
	selectedAvailableFields: FieldViewModel[] = [];

	fieldLayoutId: string;
	fieldLayoutView: FieldLayoutViewModel;
	FieldType = FieldType;
	hoverFieldId: string = null;
	isSearch = false;
	isLog = false;
	isProduction = false;
	canEdit = true;

	defaultFieldIds: string[] = [];

	constructor(private dialogRef: MatDialogRef<SelectFieldsDialogComponent>, @Inject(MAT_DIALOG_DATA) data, private fieldLayoutViewModelService: FieldLayoutViewModelService) {
		super();
		this.fieldLayoutView = data.view;
		this.isSearch = data.isSearch;
		this.isLog = !!data?.isLog;
		this.isProduction = !!data?.isProduction;
		this.canEdit = data.canEdit == null ? true : data.canEdit;
		this.fieldLayoutId = this.fieldLayoutView.fieldLayoutId;
		fieldLayoutViewModelService.getAvailableFieldViewModel().subscribe(f => this.loadAvailableFields(f));
		fieldLayoutViewModelService.getDefaultFieldIds().subscribe((data: string[]) => this.defaultFieldIds = data);
	}

	private loadAvailableFields(data: FieldViewModel[]): void {
		// Remove custom fields and keep only custom sections. Sort by build in fields alphabetically
		// followed by custom sections alphabetically.
		this.allFields = data;
		const builtInFields = data.filter((item: FieldViewModel) => item.fieldType < 0)
			.filter(f => this.isSearch || f.fieldId !== BuiltInFieldId.Score)
			.filter(f => this.isProduction ? f.isPartOfLoadFile : true)
			.sort((a: FieldViewModel, b: FieldViewModel) => a.displayTitle.localeCompare(b.displayTitle));

		const customSections = data.filter((item: FieldViewModel) => { return item.fieldType === FieldType.SectionSeparator });

		this.availableFields = builtInFields.concat(customSections);
		this.availableFieldsFiltered = this.availableFields;

		const selectedFields: FieldViewModel[] = [];
		this.fieldLayoutView.fields.forEach((inField) => {
			const field = this.allFields.find(f => f.fieldId === inField.fieldId);
			field.width = inField.width;
			selectedFields.push(field);
		});

		let collapsedSelectedFields = selectedFields;
		this.availableFields.filter(f => f.fieldType === FieldType.SectionSeparator).forEach(f => f.expanded = false);
		collapsedSelectedFields.filter(f => f.fieldType === FieldType.SectionSeparator).forEach(f => f.expanded = true);

		/**
		 *  Collapse sections if their children are in the correct order after them
		 */
		// Use comma separated string of fieldIds as a representation of the list of selected fields in their order
		const selectedString = selectedFields.map(s => s.fieldId).toString();
		selectedFields.forEach(sf => {
			if (sf.fieldType !== 0) return;

			// Find the section index
			const sectionIndex = data.findIndex(f => f.fieldId === sf.fieldId) + 1;
			// Find the next section index offset or the end of the list starting from the current section
			const nextSectionIndexOffset = data.slice(sectionIndex).findIndex(f => f.fieldType === FieldType.SectionSeparator);
			// If it's the end of the list (not found), set end index to the end of the list, else set the end index in the original array
			const nextSectionIndex = nextSectionIndexOffset === -1 ? data.length : sectionIndex + nextSectionIndexOffset;
			// Get section and subsections into an array
			const subElements = data.slice(sectionIndex - 1, nextSectionIndex);
			// Convert to comma separated string of section and subFields field ids
			const subElementString = subElements.map(s => s.fieldId).toString();

			// If this string is found in the selected list string then it is collapsible
			if (selectedString.indexOf(subElementString) > -1) {
				sf.expanded = false;
				collapsedSelectedFields = collapsedSelectedFields.filter(f => f.parentFieldId !== sf.fieldId);
			}
		});

		this.selectedAvailableFields = this.selectedFields = this.fieldLayoutView.fields = collapsedSelectedFields;
		if(selectedFields.length == 0 && this.isProduction && this.canEdit) {
			this.useDefault();
		}
	}

	public search(value: string): void {
		if (value.length === 0) {
			this.availableFieldsFiltered = this.availableFields;
		} else {
			this.availableFieldsFiltered = this.availableFields.filter((item: FieldViewModel) => {
				return item.displayTitle.toLowerCase().includes(value.toLowerCase());
			});
		}
		this.selectedAvailableFields = this.selectedFields;
	}

	public drop(event: CdkDragDrop<FieldViewModel[]>) {
		moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
	}

	public ok() {
		const sectionsToExpand = this.selectedFields.filter(f => f.fieldType === FieldType.SectionSeparator && f.expanded === false);
		sectionsToExpand.forEach(f => {
			const index = this.selectedFields.indexOf(f);
			this.selectedFields.splice(index + 1, 0, ...this.allFields.filter(af => af.parentFieldId === f.fieldId));
		});

		// Force-add DocTitle - users aren't allowed to remove this field
		if (!this.isLog && this.selectedFields.every(f => f.fieldId !== BuiltInFieldId.DocTitle)) {
			this.selectedFields.unshift(this.availableFields.find(f => f.fieldId === BuiltInFieldId.DocTitle));
		}

		// Force-add Score after Title if Search - users aren't allowed to remove this field
		if (this.isSearch && !this.selectedFields.find(f => f.fieldId === BuiltInFieldId.Score)) {
			const titleIdx = this.selectedFields.findIndex(f => f.fieldId === BuiltInFieldId.DocTitle);
			this.selectedFields.splice(titleIdx + 1, 0, this.allFields.find(f => f.fieldId === BuiltInFieldId.Score));
		}

		this.fieldLayoutView.fields = this.selectedFields;
		this.fieldLayoutView.fields.forEach((f, i) => {
			f.fieldLayoutId = this.fieldLayoutView.fieldLayoutId;
			f.rank = i;
		});

		this.dialogRef.close(this.fieldLayoutView);
	}

	public useDefault() {
		this.selectedAvailableFields = this.selectedFields = 
			this.availableFields.
			filter(af => this.isProduction ? af.isLoadFileDefault : this.defaultFieldIds.includes(af.fieldId)).
			sort((a: FieldViewModel, b: FieldViewModel) => a.rank - b.rank);
	}

	public onAvailableFieldsSelectionChange(event: MatSelectionListChange) {
		event.options.forEach((option) => {
			if (option.selected) {
				this.addToSelected(option.value);
			} else {
				this.removeFromSelected(option.value, true);
			}
		})
	}

	public expand(field: FieldViewModel) {
		field.expanded = true;
		const children = this.allFields.filter(f => f.parentFieldId === field.fieldId);
		const parentIndex = this.selectedFields.indexOf(field);
		this.selectedFields.splice(parentIndex + 1, 0, ...children);
	}

	public collapse(field: FieldViewModel) {
		field.expanded = false;
		this.removeChildrenByParentId(field.fieldId);
	}

	public selectAll() {
		this.selectedFields = this.selectedFields.concat(this.availableFields.filter(af => !this.selectedFields.includes(af)));
		this.selectedAvailableFields = this.selectedFields;
	}

	public deselectAll() {
		this.selectedFields = [];
		this.selectedAvailableFields = this.selectedFields;
		this.availableFields.filter(f => f.fieldType === FieldType.SectionSeparator).forEach(f => f.expanded = false);
	}

	public removeFromSelected(field: FieldViewModel, removeChildren: boolean = false) {
		const idx = this.selectedFields.findIndex((f: FieldViewModel) => f.fieldId === field.fieldId);
		if (idx === -1) return;

		this.selectedFields.splice(idx, 1);

		if (field.fieldType === FieldType.SectionSeparator) {
			field.expanded = false;
			if (removeChildren)
				this.removeChildrenByParentId(field.fieldId);
		}

		this.selectedAvailableFields = this.selectedAvailableFields.filter(f => f.fieldId !== field.fieldId);
	}

	private addToSelected(field: FieldViewModel) {
		const idx = this.selectedFields.findIndex((f: FieldViewModel) => f.fieldId === field.fieldId);
		if (idx !== -1) return;

		this.removeChildrenByParentId(field.fieldId);
		this.selectedFields.push(field);
	}

	private removeChildrenByParentId(parentId: string) {
		this.selectedFields = this.selectedFields.filter(f => f.parentFieldId !== parentId);
	}
}
